import { useState } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import VendorService from "@services/VendorService";
import ContactsService from "@services/ContactsService";
import AccountNotesService from "@services/AccountNotesService";
import IAccount_Note from "@models/IAccount_Note";
import IVendorModel from "@models/IVendorModel";
import IContact from "@models/IContact";
import ICreateAccountNoteDTO from "@models/DTO/ICreateAccountNoteDTO";
import IUpdateAccountNoteDTO from "@models/DTO/IUpdateAccountNoteDTO";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    closeSidebarFn: () => void;
    successFn: () => void;
    existingNote?: IAccount_Note;
};

export const AccountNoteSidebar = (props: Props) => {
    const { userModel, supplierModel } = useAppContextNonNull();

    const isEditMode = !!props.existingNote;

    // Vendor state
    const [foundVendors, setFoundVendors] = useState<IVendorModel[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel | null>(props.existingNote?.Vendor || null);

    // Contact state
    const [foundContacts, setFoundContacts] = useState<IContact[]>([]);
    const [selectedContact, setSelectedContact] = useState<IContact | null>(props.existingNote?.Contact || null);
    const [resetContactTypeahead, setResetContactTypeahead] = useState<boolean>(false);

    // Note state
    const [noteText, setNoteText] = useState<string>(props.existingNote?.note || "");

    // Saving state
    const [isSaving, setIsSaving] = useState<boolean>(false);

    const findVendors = async (name: string) => {
        try {
            const data = await VendorService.getVendors({ name, limit: "25" }, userModel, supplierModel);
            setFoundVendors(data.supplierVendorTokens.map((t) => t.Vendor!));
        } catch (error) {
            console.error("Error finding vendors:", error);
        }
    };

    const onVendorSelected = (vendor: IVendorModel) => {
        setFoundVendors([]);
        setSelectedVendor(vendor);
        // Clear contact when vendor changes
        setSelectedContact(null);
        setFoundContacts([]);
        setResetContactTypeahead(true);
    };

    const findContacts = async (name: string) => {
        if (!selectedVendor) return;

        try {
            const contacts = await ContactsService.getContacts(name, userModel, supplierModel, selectedVendor.id);
            setFoundContacts(contacts);
        } catch (error) {
            console.error("Error finding contacts:", error);
        }
    };

    const onContactSelected = (contact: IContact) => {
        setFoundContacts([]);
        setSelectedContact(contact);
    };

    const saveNote = async () => {
        if (!selectedVendor || !noteText.trim()) return;

        setIsSaving(true);

        try {
            if (isEditMode && props.existingNote) {
                const dto: IUpdateAccountNoteDTO = {
                    details: {
                        vendor_id: selectedVendor.id,
                        contact_id: selectedContact?.id,
                        note: noteText.trim(),
                    },
                };
                await AccountNotesService.updateAccountNote(props.existingNote.id, dto, userModel, supplierModel);
            } else {
                const dto: ICreateAccountNoteDTO = {
                    details: {
                        vendor_id: selectedVendor.id,
                        contact_id: selectedContact?.id,
                        note: noteText.trim(),
                        supplier_id: supplierModel.id,
                    },
                };
                await AccountNotesService.createAccountNote(dto, userModel, supplierModel);
            }

            props.successFn();
        } catch (error) {
            console.error("Error saving account note:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const deleteNote = async () => {
        if (!props.existingNote) return;

        setIsSaving(true);

        try {
            await AccountNotesService.deleteAccountNote(props.existingNote.id, userModel, supplierModel);
            props.successFn();
        } catch (error) {
            console.error("Error deleting account note:", error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={"AccountNoteSidebar m-4"}>
            <div className={"d-flex justify-content-between align-items-center"}>
                <OwTitle title={"note"} leftIconName={"fad fa-sticky-note"} />
                <OwButton fn={props.closeSidebarFn} centerIconClass={"fas fa-times"} className={"btn-danger"} />
            </div>

            <div className="mt-4">
                <OwLabel title={"customer"} isSupplierVendorOverride={true} supplierModel={supplierModel} />
                <TypeaheadFn
                    lookupFn={findVendors}
                    availableOptions={foundVendors}
                    selectedOption={selectedVendor}
                    selectedOptionFn={onVendorSelected}
                    titleProp="name"
                    placeholder={"search customers..."}
                />
            </div>

            <div className="mt-4">
                <OwLabel title={"contact"} />
                <TypeaheadFn
                    lookupFn={findContacts}
                    availableOptions={foundContacts}
                    selectedOption={selectedContact}
                    selectedOptionFn={onContactSelected}
                    titleProp="name"
                    subTitleProp="email"
                    placeholder={"search contacts..."}
                    disabled={!selectedVendor}
                    resetTypeahead={resetContactTypeahead}
                    resetTypeaheadCallbackFn={() => setResetContactTypeahead(false)}
                />
            </div>

            <div className="mt-4">
                <OwLabel title={"note"} />
                <textarea
                    className="form-control"
                    rows={6}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder={"Enter note..."}
                />
            </div>

            <div className="mt-4 d-flex gap-2">
                <OwButton
                    title={"save"}
                    fn={saveNote}
                    className={"btn-primary"}
                    disabled={isSaving || !selectedVendor || !noteText.trim()}
                />
            </div>
        </div>
    );
};
